import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  Filter, Plus, Search, MoreVertical, Edit, Trash2, Users, RefreshCw,
  Eye, ChevronRight, Play, Zap, X, Copy
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Segment field options - expanded with more fields
const FIELD_OPTIONS = [
  // Contact Info
  { value: 'first_name', label: 'First Name', type: 'text', category: 'Contact Info' },
  { value: 'last_name', label: 'Last Name', type: 'text', category: 'Contact Info' },
  { value: 'name', label: 'Full Name', type: 'text', category: 'Contact Info' },
  { value: 'email', label: 'Email', type: 'text', category: 'Contact Info' },
  { value: 'phone', label: 'Phone', type: 'text', category: 'Contact Info' },
  { value: 'mobile_number', label: 'Mobile Number', type: 'text', category: 'Contact Info' },
  { value: 'company', label: 'Company', type: 'text', category: 'Contact Info' },
  { value: 'job_title', label: 'Job Title', type: 'text', category: 'Contact Info' },
  
  // Location
  { value: 'city', label: 'City', type: 'text', category: 'Location' },
  { value: 'state', label: 'State/Province', type: 'text', category: 'Location' },
  { value: 'country', label: 'Country', type: 'text', category: 'Location' },
  { value: 'address', label: 'Address', type: 'text', category: 'Location' },
  
  // Lead Properties
  { value: 'source', label: 'Lead Source', type: 'text', category: 'Lead Properties' },
  { value: 'status', label: 'Lead Status', type: 'text', category: 'Lead Properties' },
  { value: 'lifecycle_stage', label: 'Lifecycle Stage', type: 'text', category: 'Lead Properties' },
  { value: 'owner_id', label: 'Owner', type: 'text', category: 'Lead Properties' },
  { value: 'lead_score', label: 'Lead Score', type: 'number', category: 'Lead Properties' },
  
  // Engagement
  { value: 'last_contacted_at', label: 'Last Contacted', type: 'date', category: 'Engagement' },
  { value: 'last_activity_at', label: 'Last Activity', type: 'date', category: 'Engagement' },
  { value: 'email_opens', label: 'Email Opens', type: 'number', category: 'Engagement' },
  { value: 'email_clicks', label: 'Email Clicks', type: 'number', category: 'Engagement' },
  
  // Tags & Lists
  { value: 'tags', label: 'Has Tag', type: 'tag', category: 'Tags & Lists' },
  { value: 'list_id', label: 'In List', type: 'list', category: 'Tags & Lists' },
  
  // Dates
  { value: 'created_at', label: 'Created Date', type: 'date', category: 'Dates' },
  { value: 'updated_at', label: 'Updated Date', type: 'date', category: 'Dates' },
];

const OPERATORS = {
  text: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
    { value: 'in', label: 'is any of' },
  ],
  date: [
    { value: 'equals', label: 'is' },
    { value: 'before', label: 'is before' },
    { value: 'after', label: 'is after' },
    { value: 'between', label: 'is between' },
    { value: 'in_last', label: 'in the last' },
    { value: 'not_in_last', label: 'not in the last' },
  ],
  number: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than', label: 'less than' },
    { value: 'between', label: 'between' },
  ],
  tag: [
    { value: 'has_tag', label: 'has tag' },
    { value: 'not_has_tag', label: 'does not have tag' },
    { value: 'has_any_tag', label: 'has any tag' },
    { value: 'has_no_tags', label: 'has no tags' },
  ],
  list: [
    { value: 'in_list', label: 'is in list' },
    { value: 'not_in_list', label: 'is not in list' },
  ]
};

// Group fields by category for better UX
const getFieldsByCategory = () => {
  const grouped = {};
  FIELD_OPTIONS.forEach(field => {
    const cat = field.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(field);
  });
  return grouped;
};

export default function MarketingSegments() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Reference data for dropdowns
  const [availableTags, setAvailableTags] = useState([]);
  const [availableLists, setAvailableLists] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [lifecycleStages, setLifecycleStages] = useState([]);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    criteria: {
      filters: [],
      logic: 'and'
    }
  });
  
  const token = sessionStorage.getItem('auth_token');
  const limit = 20;

  useEffect(() => {
    if (token) {
      fetchSegments();
    }
  }, [token, page, search]);

  // Fetch reference data for filter value dropdowns
  useEffect(() => {
    if (token) {
      fetchReferenceData();
    }
  }, [token]);

  const fetchReferenceData = async () => {
    try {
      // Fetch tags
      const tagsRes = await axios.get(`${API}/api/master/tags?token=${token}&include_inactive=false`);
      if (tagsRes.data.tags) setAvailableTags(tagsRes.data.tags);
      
      // Fetch lists
      const listsRes = await axios.get(`${API}/api/marketing/lists?token=${token}&limit=100`);
      if (listsRes.data.lists) setAvailableLists(listsRes.data.lists);
      
      // Fetch lead statuses
      const statusRes = await axios.get(`${API}/api/contacts/lead-statuses?token=${token}`);
      if (statusRes.data.statuses) setLeadStatuses(statusRes.data.statuses);
      
      // Fetch lifecycle stages
      const stagesRes = await axios.get(`${API}/api/contacts/lifecycle-stages?token=${token}`);
      if (stagesRes.data.stages) setLifecycleStages(stagesRes.data.stages);
    } catch (error) {
      console.error('Failed to fetch reference data', error);
    }
  };

  const fetchSegments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        token,
        page: page.toString(),
        limit: limit.toString()
      });
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/api/marketing/segments?${params}`);
      if (response.data.success) {
        setSegments(response.data.segments);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch segments', error);
      toast.error('Failed to load segments');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (segment = null) => {
    if (segment) {
      setEditingSegment(segment);
      setForm({
        name: segment.name,
        description: segment.description || '',
        criteria: segment.criteria || { filters: [], logic: 'and' }
      });
    } else {
      setEditingSegment(null);
      setForm({
        name: '',
        description: '',
        criteria: { filters: [], logic: 'and' }
      });
    }
    setPreviewData(null);
    setDialogOpen(true);
  };

  const saveSegment = async () => {
    if (!form.name.trim()) {
      toast.error('Segment name is required');
      return;
    }
    
    if (form.criteria.filters.length === 0) {
      toast.error('Add at least one filter condition');
      return;
    }
    
    try {
      setSaving(true);
      if (editingSegment) {
        await axios.put(
          `${API}/api/marketing/segments/${editingSegment.id}?token=${token}`,
          form
        );
        toast.success('Segment updated');
      } else {
        await axios.post(`${API}/api/marketing/segments?token=${token}`, form);
        toast.success('Segment created');
      }
      setDialogOpen(false);
      fetchSegments();
    } catch (error) {
      toast.error('Failed to save segment');
    } finally {
      setSaving(false);
    }
  };

  const deleteSegment = async (segment) => {
    if (!window.confirm(`Delete segment "${segment.name}"? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/api/marketing/segments/${segment.id}?token=${token}`);
      toast.success('Segment deleted');
      fetchSegments();
    } catch (error) {
      toast.error('Failed to delete segment');
    }
  };

  const recalculateSegment = async (segment) => {
    try {
      const response = await axios.post(
        `${API}/api/marketing/segments/${segment.id}/recalculate?token=${token}`
      );
      if (response.data.success) {
        toast.success(`Segment has ${response.data.contact_count} contacts`);
        fetchSegments();
      }
    } catch (error) {
      toast.error('Failed to recalculate segment');
    }
  };

  const addFilter = () => {
    setForm(prev => ({
      ...prev,
      criteria: {
        ...prev.criteria,
        filters: [
          ...prev.criteria.filters,
          { field: '', operator: '', value: '' }
        ]
      }
    }));
  };

  const updateFilter = (index, updates) => {
    setForm(prev => ({
      ...prev,
      criteria: {
        ...prev.criteria,
        filters: prev.criteria.filters.map((f, i) => 
          i === index ? { ...f, ...updates } : f
        )
      }
    }));
  };

  const removeFilter = (index) => {
    setForm(prev => ({
      ...prev,
      criteria: {
        ...prev.criteria,
        filters: prev.criteria.filters.filter((_, i) => i !== index)
      }
    }));
  };

  const previewSegment = async () => {
    if (form.criteria.filters.length === 0) {
      toast.error('Add at least one filter condition');
      return;
    }
    
    try {
      setPreviewLoading(true);
      const response = await axios.post(
        `${API}/api/marketing/segments/preview?token=${token}`,
        { criteria: form.criteria, limit: 10 }
      );
      if (response.data.success) {
        setPreviewData(response.data);
      }
    } catch (error) {
      toast.error('Failed to preview segment');
    } finally {
      setPreviewLoading(false);
    }
  };

  const getFieldType = (field) => {
    return FIELD_OPTIONS.find(f => f.value === field)?.type || 'text';
  };

  const getOperators = (field) => {
    const type = getFieldType(field);
    return OPERATORS[type] || OPERATORS.text;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="marketing-segments">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Segments</h1>
          <p className="text-gray-500 mt-1">
            Create dynamic audience segments based on contact attributes
          </p>
        </div>
        <Button onClick={() => openDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Create Segment
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search segments..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={fetchSegments}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Segments Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : segments.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Filter className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No segments yet</h3>
            <p className="text-gray-500 mb-6">Create dynamic segments to target specific audiences</p>
            <Button onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Segment
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {segments.map((segment) => (
            <Card key={segment.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Filter className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{segment.name}</CardTitle>
                      {segment.description && (
                        <CardDescription className="text-xs mt-1 line-clamp-1">
                          {segment.description}
                        </CardDescription>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/marketing/segments/${segment.id}`)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Contacts
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => recalculateSegment(segment)}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Recalculate
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openDialog(segment)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => deleteSegment(segment)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Users className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {segment.contact_count || 0} contacts
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate(`/marketing/segments/${segment.id}`)}
                  >
                    View
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
                {segment.criteria?.filters?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500">
                      {segment.criteria.filters.length} filter{segment.criteria.filters.length > 1 ? 's' : ''} 
                      {' '}({segment.criteria.logic?.toUpperCase() || 'AND'})
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} segments
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSegment ? 'Edit Segment' : 'Create Segment'}</DialogTitle>
            <DialogDescription>
              Define conditions to dynamically group contacts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Segment Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., High-Value Leads"
                />
              </div>
              <div className="space-y-2">
                <Label>Filter Logic</Label>
                <Select
                  value={form.criteria.logic}
                  onValueChange={(value) => setForm(prev => ({
                    ...prev,
                    criteria: { ...prev.criteria, logic: value }
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="and">Match ALL conditions (AND)</SelectItem>
                    <SelectItem value="or">Match ANY condition (OR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                rows={2}
              />
            </div>

            {/* Filter Conditions */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Filter Conditions</Label>
                <Button type="button" variant="outline" size="sm" onClick={addFilter}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Condition
                </Button>
              </div>
              
              {form.criteria.filters.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed rounded-lg text-gray-400">
                  <Filter className="h-8 w-8 mx-auto mb-2" />
                  <p>No conditions added yet</p>
                  <Button 
                    type="button" 
                    variant="link" 
                    size="sm" 
                    onClick={addFilter}
                    className="mt-2"
                  >
                    Add your first condition
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {form.criteria.filters.map((filter, index) => {
                    const fieldType = getFieldType(filter.field);
                    const needsValueInput = filter.operator && !['is_empty', 'is_not_empty', 'has_any_tag', 'has_no_tags'].includes(filter.operator);
                    
                    return (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg flex-wrap">
                      {index > 0 && (
                        <Badge variant="outline" className="shrink-0">
                          {form.criteria.logic?.toUpperCase() || 'AND'}
                        </Badge>
                      )}
                      
                      {/* Field Select with Categories */}
                      <Select
                        value={filter.field}
                        onValueChange={(value) => updateFilter(index, { field: value, operator: '', value: '' })}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Select field" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(getFieldsByCategory()).map(([category, fields]) => (
                            <div key={category}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-100">{category}</div>
                              {fields.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Operator Select */}
                      <Select
                        value={filter.operator}
                        onValueChange={(value) => updateFilter(index, { operator: value })}
                        disabled={!filter.field}
                      >
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Operator" />
                        </SelectTrigger>
                        <SelectContent>
                          {getOperators(filter.field).map(op => (
                            <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Value Input - varies by field type */}
                      {needsValueInput && fieldType === 'tag' && (
                        <Select
                          value={filter.value}
                          onValueChange={(value) => updateFilter(index, { value })}
                        >
                          <SelectTrigger className="w-[160px]">
                            <SelectValue placeholder="Select tag" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableTags.map(tag => (
                              <SelectItem key={tag.id} value={tag.name}>
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color || '#6366F1' }} />
                                  {tag.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {needsValueInput && fieldType === 'list' && (
                        <Select
                          value={filter.value}
                          onValueChange={(value) => updateFilter(index, { value })}
                        >
                          <SelectTrigger className="w-[160px]">
                            <SelectValue placeholder="Select list" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableLists.map(list => (
                              <SelectItem key={list.id} value={list.id}>{list.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {needsValueInput && filter.field === 'status' && (
                        <Select
                          value={filter.value}
                          onValueChange={(value) => updateFilter(index, { value })}
                        >
                          <SelectTrigger className="w-[160px]">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            {leadStatuses.map(status => (
                              <SelectItem key={status.value || status.name} value={status.value || status.name}>
                                {status.name || status.display_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {needsValueInput && filter.field === 'lifecycle_stage' && (
                        <Select
                          value={filter.value}
                          onValueChange={(value) => updateFilter(index, { value })}
                        >
                          <SelectTrigger className="w-[160px]">
                            <SelectValue placeholder="Select stage" />
                          </SelectTrigger>
                          <SelectContent>
                            {lifecycleStages.map(stage => (
                              <SelectItem key={stage.name} value={stage.name}>
                                {stage.display_name || stage.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {needsValueInput && fieldType === 'date' && (
                        <Input
                          type="date"
                          value={filter.value}
                          onChange={(e) => updateFilter(index, { value: e.target.value })}
                          className="w-[160px]"
                        />
                      )}
                      
                      {needsValueInput && fieldType === 'number' && (
                        <Input
                          type="number"
                          value={filter.value}
                          onChange={(e) => updateFilter(index, { value: e.target.value })}
                          placeholder="Value"
                          className="w-[120px]"
                        />
                      )}
                      
                      {needsValueInput && fieldType === 'text' && !['status', 'lifecycle_stage'].includes(filter.field) && (
                        <Input
                          value={filter.value}
                          onChange={(e) => updateFilter(index, { value: e.target.value })}
                          placeholder="Value"
                          className="flex-1 min-w-[120px]"
                        />
                      )}
                      
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFilter(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <Label>Preview</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={previewSegment}
                  disabled={previewLoading || form.criteria.filters.length === 0}
                >
                  {previewLoading ? (
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-1" />
                  )}
                  Preview Contacts
                </Button>
              </div>
              
              {previewData && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-3">
                    <span className="text-lg text-purple-600">{previewData.total}</span> contacts match this segment
                  </p>
                  {previewData.preview?.length > 0 && (
                    <div className="space-y-2">
                      {previewData.preview.map((contact, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-medium">
                            {contact.first_name?.[0] || contact.email?.[0] || '?'}
                          </div>
                          <div>
                            <p className="font-medium">
                              {contact.first_name} {contact.last_name}
                            </p>
                            <p className="text-gray-500 text-xs">{contact.email}</p>
                          </div>
                        </div>
                      ))}
                      {previewData.total > 10 && (
                        <p className="text-xs text-gray-500 pt-2">
                          And {previewData.total - 10} more contacts...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveSegment} disabled={saving}>
              {saving ? 'Saving...' : (editingSegment ? 'Update Segment' : 'Create Segment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
